import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PaperPlaneTilt, DownloadSimple, ShareNetwork, CheckCircle, Receipt, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, downloadAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const STATUS = {
  draft: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  viewed: "bg-sky-100 text-sky-700",
  accepted: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  expired: "bg-zinc-200 text-zinc-600",
};

export default function EstimatePreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [est, setEst] = useState(null);
  const [cfg, setCfg] = useState({ sms_enabled: false, email_enabled: false });
  const [sendOpen, setSendOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get(`/estimates/${id}`).then((response) => {
      setEst(response.data);
      setEmailTo(response.data.customer_email || "");
      setSmsTo(response.data.customer_phone || "");
    }).catch(() => toast.error("Estimate not found"));
  };

  useEffect(() => {
    load();
    api.get("/config").then((response) => setCfg(response.data)).catch(() => {});
  }, [id]);

  if (!est) return <div className="p-10 text-center text-zinc-400">Loading…</div>;
  const locked = est.locked || est.status === "accepted";
  const shareUrl = `${window.location.origin}/e/${est.share_token}`;

  const patch = (next) => setEst({ ...est, ...next });

  const save = async () => {
    setBusy("save");
    try {
      const response = await api.put(`/estimates/${id}`, est);
      setEst(response.data);
      toast.success("Estimate saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't save");
    }
    setBusy("");
  };

  const markSent = async () => {
    try {
      await api.post(`/estimates/${id}/status`, { status: "sent" });
      toast.success("Marked sent");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't update status");
    }
  };

  const sendEmail = async () => {
    setBusy("email");
    try {
      await api.post(`/estimates/${id}/email`, { email: emailTo, share_url: shareUrl });
      toast.success(`Emailed to ${emailTo}`);
      setSendOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email failed");
    }
    setBusy("");
  };

  const sendSms = async () => {
    setBusy("sms");
    try {
      await api.post(`/estimates/${id}/sms`, { phone: smsTo, share_url: shareUrl });
      toast.success(`Texted to ${smsTo}`);
      setSendOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Text failed");
    }
    setBusy("");
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Estimate link copied");
  };

  const convert = async () => {
    setBusy("convert");
    try {
      const response = await api.post(`/estimates/${id}/convert`);
      toast.success("Invoice created");
      nav(`/invoice/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't convert");
    }
    setBusy("");
  };

  const setLine = (index, next) => {
    const line_items = est.line_items.map((line, i) => (i === index ? { ...line, ...next } : line));
    patch({ line_items });
  };

  return (
    <div className="min-h-screen" data-testid="estimate-preview">
      <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="estimate-back" onClick={() => nav(est.job_id ? `/job/${est.job_id}` : "/history")} className="text-zinc-500"><ArrowLeft size={24} /></button>
        <div className="min-w-0">
          <h1 className="font-heading text-xl font-black tracking-tight truncate">{est.estimate_number || "Estimate"}</h1>
          <p className="text-xs text-zinc-500 truncate">{est.customer_name || "No client"}</p>
        </div>
        <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-1 rounded-full ${STATUS[est.status] || "bg-zinc-100 text-zinc-600"}`} data-testid="estimate-owner-status">
          {(est.status || "").replace("_", " ")}
        </span>
      </header>

      <div className="p-4 space-y-4">
        {locked && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3" data-testid="estimate-locked-note">
            <p className="text-sm font-semibold text-green-800">Signed by {est.signature?.signer_name}. This estimate is locked.</p>
            {est.signature?.signed_at && <p className="text-xs text-green-700 mt-1">{new Date(est.signature.signed_at).toLocaleString()}</p>}
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3">
          {(est.line_items || []).map((line, index) => (
            <div key={index} className="grid grid-cols-[1fr_88px] gap-2 items-center">
              <Input disabled={locked} value={line.description} onChange={(e) => setLine(index, { description: e.target.value })} className="h-11" />
              <Input disabled={locked} type="number" value={line.amount} onChange={(e) => setLine(index, { amount: Number(e.target.value), unit_price: Number(e.target.value) })} className="h-11 font-semibold" />
            </div>
          ))}
          {(est.materials || []).filter((mat) => !mat.customer_supplied).map((mat, index) => (
            <div key={`m${index}`} className="flex justify-between text-sm text-zinc-600">
              <span>{mat.name}</span>
              <span className="font-mono-num font-semibold">{money(mat.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between items-end border-t border-zinc-100 pt-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-zinc-400">Valid until</p>
              <Input disabled={locked} type="date" value={est.valid_until || ""} onChange={(e) => patch({ valid_until: e.target.value })} className="h-10 mt-1" />
            </div>
            <p className="font-heading font-black text-3xl" data-testid="estimate-owner-total">{money(est.total)}</p>
          </div>
          <Textarea disabled={locked} value={est.notes || ""} onChange={(e) => patch({ notes: e.target.value })} rows={2} placeholder="Note to the client" />
          {!locked && <Button data-testid="estimate-save" onClick={save} variant="outline" className="w-full h-11 font-bold">{busy === "save" ? "Saving…" : "Save draft"}</Button>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="estimate-pdf" variant="outline" className="h-12 font-bold" onClick={() => downloadAuthed(`/estimates/${id}/pdf`, `${est.estimate_number || "estimate"}.pdf`)}><DownloadSimple size={18} /> PDF</Button>
          <Button data-testid="estimate-copy" variant="outline" className="h-12 font-bold" onClick={copyLink}><ShareNetwork size={18} /> Copy link</Button>
          {est.status === "draft" && <Button data-testid="estimate-mark-sent" variant="outline" className="h-12 font-bold" onClick={markSent}><CheckCircle size={18} /> Mark sent</Button>}
          {!locked && <Button data-testid="estimate-send" className="h-12 font-bold" onClick={() => setSendOpen(true)}><PaperPlaneTilt size={18} /> Send</Button>}
          {est.status === "accepted" && (
            <Button data-testid="estimate-convert" className="h-12 font-bold col-span-2" onClick={() => (est.invoice_id ? nav(`/invoice/${est.invoice_id}`) : convert())} disabled={busy === "convert"}>
              <Receipt size={18} /> {est.invoice_id ? "Open invoice" : "Convert to invoice"}
            </Button>
          )}
          {est.status === "accepted" && est.job_id && (
            <Button data-testid="estimate-schedule" variant="outline" className="h-12 font-bold col-span-2" onClick={() => nav(`/job/${est.job_id}?schedule=1`)}>
              <CalendarBlank size={18} /> Schedule this job
            </Button>
          )}
        </div>
      </div>

      <Drawer open={sendOpen} onOpenChange={setSendOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Send estimate</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-3">
            <div className="flex gap-2">
              <Input data-testid="estimate-email" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@email.com" className="h-12" />
              <Button data-testid="estimate-send-email" onClick={sendEmail} disabled={!emailTo || !cfg.email_enabled} className="h-12 font-bold">Email</Button>
            </div>
            {!cfg.email_enabled && <p className="text-xs text-zinc-400">Add Resend to email estimates.</p>}
            <div className="flex gap-2">
              <Input data-testid="estimate-sms" value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="+15555550100" className="h-12" disabled={!cfg.sms_enabled} />
              <Button data-testid="estimate-send-sms" variant="outline" onClick={sendSms} disabled={!smsTo || !cfg.sms_enabled} className="h-12 font-bold">Text</Button>
            </div>
            {!cfg.sms_enabled && <p className="text-xs text-zinc-400">Add Twilio to text estimates.</p>}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
